import React, { useRef, useEffect, startTransition, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  selectedConversationIdAtom,
  fetchConversationAtom,
  messageCountAtom
} from '../../atoms/v3/chatAtoms';
import { globalTaskEditAtom } from '../../atoms/v3/taskAtoms';
import { chatInputFocusedAtom } from '../../atoms/v3/chatInputAtoms';
import {
  backgroundTypeAtom,
  backgroundImagePathAtom,
  backgroundMediaTypeAtom,
  backgroundObjectPositionAtom,
  backgroundNeedsCrossfadeLoopAtom
} from '../../atoms/v3/chatPersonalizationAtoms';
import { getPlatformInfo } from '../../utils/platformUtils';

import { ChatInputContainer } from './components/chatInput/ChatInputContainer';
import { ChatSkeletonLoader } from './components/chat/ChatSkeletonLoader';
import { MessageList } from './components/chatMessage/MessageList';
import { NewConversationPrompts } from './components/chat/NewConversationPrompts';
import { ChatHeader } from './components/chatHeader';
import { TaskDetailSheet } from './components/tasks/TaskDetailSheet';
import { ChatBackgroundRenderer } from '../../components/v3/ChatBackgroundRenderer';
import { DragDropOverlay } from './components/chatInput/DragDropOverlay';
import { useDragAndDrop } from '../../hooks/v3/useDragAndDrop';
import { usePasteUpload } from '../../hooks/v3/usePasteUpload';
import { isTouchDevice } from '../../utils/platformUtils';

interface ChatDetailProps {
  isEmbedded?: boolean;
}

export const ChatDetail: React.FC<ChatDetailProps> = ({ isEmbedded = false }) => {

  const [selectedConversationId] = useAtom(selectedConversationIdAtom);

  const id = selectedConversationId;

  const inputContainerRef = useRef<HTMLDivElement>(null);

  const fetchConversation = useSetAtom(fetchConversationAtom);
  const messageCount = useAtomValue(messageCountAtom);
  const chatInputFocused = useAtomValue(chatInputFocusedAtom);

  // 背景个性化设置
  const backgroundType = useAtomValue(backgroundTypeAtom);
  const backgroundImagePath = useAtomValue(backgroundImagePathAtom);
  const backgroundMediaType = useAtomValue(backgroundMediaTypeAtom);
  const backgroundObjectPosition = useAtomValue(backgroundObjectPositionAtom);
  const backgroundNeedsCrossfadeLoop = useAtomValue(backgroundNeedsCrossfadeLoopAtom);

  // 检测新对话状态：没有选中对话ID且消息数为0
  const isNewConversation = !id && messageCount === 0;

  // Chat 页面快捷键现在由 AppV3 中的 useShortcutRegistration 统一管理



  // 任务编辑状态
  const [globalTaskEdit, setGlobalTaskEdit] = useAtom(globalTaskEditAtom);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);

  // 只保留获取对话数据的逻辑
  useEffect(() => {
    if (id) {
      startTransition(() => {
        console.log(new Error('[ChatDetail] Triggering fetchConversation'), 'id:', id);
        fetchConversation(id);
      });
    }
  }, [id, fetchConversation]);

  // 监听全局任务编辑状态 - 只在非嵌入模式下响应，避免与 Tasks 页面冲突
  useEffect(() => {
    // 只有在非嵌入模式（即作为主要内容显示）时才响应全局任务编辑状态
    // 嵌入模式下（如桌面三栏布局的右侧栏）让 Tasks 页面处理
    if (!isEmbedded && globalTaskEdit.isOpen && globalTaskEdit.taskId && globalTaskEdit.triggerSource === 'hyperlink') {
      // 设置选中的任务ID并打开任务详情
      setSelectedTaskId(globalTaskEdit.taskId);
      setIsTaskDetailOpen(true);

      // 重置全局状态
      setGlobalTaskEdit({
        taskId: null,
        isOpen: false,
        triggerSource: 'normal'
      });
    }
  }, [isEmbedded, globalTaskEdit, setGlobalTaskEdit]);

  // 处理任务详情关闭
  const handleCloseTaskDetail = () => {
    setIsTaskDetailOpen(false);
    setSelectedTaskId(null);
  };

  // 拖拽和粘贴上传功能
  const { isDragging, handleDragOver, handleDragLeave, handleDrop } = useDragAndDrop();
  // 启用全局粘贴监听，只在聊天页面生效
  usePasteUpload({ enableGlobalListener: true, onlyInChatPages: true });





  // Chat 页面特定的键盘快捷键处理 - 已迁移到新的快捷键系统
  // 旧的快捷键逻辑已被 useShortcutRegistration 和 useChatShortcutActions 替代
  useEffect(() => {
    // 保留注释作为参考，实际快捷键处理已迁移到新系统
    console.log('ChatDetail: Old keyboard shortcuts disabled, using new shortcut system');

    // 旧的快捷键监听器已禁用，现在由新的快捷键系统处理
    // 所有快捷键逻辑已迁移到 useShortcutRegistration 和 useChatShortcutActions
  }, []);

  // 检测是否为触摸设备，触摸设备不支持拖拽
  const isTouch = isTouchDevice();

  if ( id && messageCount === 0) {
    return <ChatSkeletonLoader isEmbedded={isEmbedded} />;
  }

  // 计算输入框的动态底部偏移量
  const platformInfo = getPlatformInfo();
  const isChatPage = true; // 当前就在聊天页面
  const isMobile = platformInfo.isIOS || platformInfo.isAndroid;
  const shouldHideTabBar = isChatPage && chatInputFocused && isMobile;

  // 动态计算底部偏移量
  const getInputBottomClass = () => {
    if (isEmbedded) {
      return 'bottom-0';
    }
    if (shouldHideTabBar) {
      // 键盘弹出且TabBar隐藏时，只保留安全区域偏移
      return 'bottom-[env(safe-area-inset-bottom)]';
    }
    // 默认情况：TabBar显示时的偏移
    return 'bottom-[calc(4rem+env(safe-area-inset-bottom))]';
  };

  return (
    <div
      className={`flex flex-col h-full relative ${backgroundType === 'solid' ? 'bg-white dark:bg-ios-dark-gray6' : ''}`}
      data-chat-detail-container="true" // 🔥 添加标识符，用于 RecordingOverlay 定位
      // 拖拽事件处理（仅在非触摸设备上启用）
      {...(!isTouch && {
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop
      })}
      // 注意：粘贴事件现在由全局监听器处理，不需要在这里设置 onPaste
    >
      {/* 背景媒体层 - 使用复用组件 */}
      <ChatBackgroundRenderer
        backgroundType={backgroundType}
        backgroundImagePath={backgroundImagePath}
        backgroundMediaType={backgroundMediaType}
        backgroundObjectPosition={backgroundObjectPosition}
        backgroundNeedsCrossfadeLoop={backgroundNeedsCrossfadeLoop}
        isNewConversation={isNewConversation}
      />

      {/* 内容层 */}
      <div className="relative z-10 flex flex-col h-full">
        {/* 拖拽覆盖层 - 仅在非触摸设备且正在拖拽时显示 */}
        {!isTouch && <DragDropOverlay isVisible={isDragging} />}

        <ChatHeader
          isEmbedded={isEmbedded}
        />

        {/* 条件渲染：新对话时显示启动界面，否则显示消息列表 */}
        {isNewConversation ? (
          <NewConversationPrompts />
        ) : (
          <MessageList />
        )}

        <div
          ref={inputContainerRef}
          className={
            isEmbedded
              ? "relative z-[60]"
              : `fixed left-0 right-0 ${getInputBottomClass()} z-[60]`
          }
        >
          <ChatInputContainer isEmbedded={isEmbedded}/>
        </div>

        {/* 任务详情浮层 */}
        <TaskDetailSheet
          taskId={selectedTaskId}
          isOpen={isTaskDetailOpen}
          onClose={handleCloseTaskDetail}
        />
      </div>


    </div>
  );
};
